/**
 * Settings → My API Keys (OAIP-96, OAIP-127)
 *
 * Self-service surface for the per-user MCP API keys that the Apex MCP
 * server uses to authenticate MCP clients (Claude Desktop, Microsoft
 * Copilot Studio, or any other MCP client). Visible to every signed-in
 * allowlisted user — no role gate.
 *
 * UX rules baked in:
 *   - The plaintext key is shown ONCE in a modal at create time, then
 *     never again. We make this very clear.
 *   - Existing keys list shows label + 8-char prefix + lifecycle dates.
 *   - Revoke is a Popconfirm; revoked keys greyed out with a tag.
 *
 * Why a per-user tab instead of admin-only:
 *   Each pilot user wires their own key into their own Copilot Studio
 *   agent. Admins can still see+revoke anyone's keys via a future admin
 *   pane (`apexMcpKeysHandler` action `admin_list_all`), but the common
 *   case is self-service.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  App,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { ReloadOutlined, KeyOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  createMcpKey,
  listMcpKeys,
  revokeMcpKey,
  type McpKey,
} from "../../lib/api";

function formatDate(value?: string | null): string {
  if (!value) return "—";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm") : value;
}

export default function ApiKeysTab() {
  const { message } = App.useApp();
  const [keys, setKeys] = useState<McpKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [label, setLabel] = useState("");
  const [creating, setCreating] = useState(false);
  const [revealedKey, setRevealedKey] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listMcpKeys();
      setKeys(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const activeCount = useMemo(
    () => keys.filter((k) => !k.revoked).length,
    [keys],
  );

  const onCreate = async () => {
    const trimmed = label.trim();
    if (!trimmed) {
      message.warning("Add a short label so you can tell your keys apart.");
      return;
    }
    setCreating(true);
    try {
      const result = await createMcpKey(trimmed);
      setRevealedKey(result.key);
      setLabel("");
      await refresh();
    } catch (err) {
      message.error(err instanceof Error ? err.message : String(err));
    } finally {
      setCreating(false);
    }
  };

  const onRevoke = async (id: string) => {
    try {
      await revokeMcpKey(id);
      message.success("Key revoked.");
      await refresh();
    } catch (err) {
      message.error(err instanceof Error ? err.message : String(err));
    }
  };

  const columns = [
    {
      title: "Label",
      dataIndex: "label",
      key: "label",
      render: (value: string, row: McpKey) => (
        <Space size="small">
          <Typography.Text style={{ opacity: row.revoked ? 0.5 : 1 }}>
            {value || "(no label)"}
          </Typography.Text>
          {row.revoked && <Tag color="default">Revoked</Tag>}
        </Space>
      ),
    },
    {
      title: "Prefix",
      dataIndex: "prefix",
      key: "prefix",
      width: 140,
      render: (value: string) => (
        <Typography.Text code style={{ fontFamily: "monospace" }}>
          {value || "—"}…
        </Typography.Text>
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      render: (v: string | null | undefined) => formatDate(v),
    },
    {
      title: "Last used",
      dataIndex: "lastUsedAt",
      key: "lastUsedAt",
      width: 180,
      render: (v: string | null | undefined) => formatDate(v),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_: unknown, row: McpKey) => {
        if (row.revoked) {
          return <Typography.Text type="secondary">—</Typography.Text>;
        }
        return (
          <Popconfirm
            title="Revoke this API key?"
            description="Any client using this key will stop working immediately."
            okText="Revoke"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
            onConfirm={() => onRevoke(row.id)}
          >
            <Button danger size="small">Revoke</Button>
          </Popconfirm>
        );
      },
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card title="What is this?">
        <Typography.Paragraph style={{ marginBottom: 8 }}>
          Apex exposes a Model Context Protocol (MCP) endpoint that lets MCP
          clients — like Claude Desktop or a Microsoft Copilot Studio agent in
          Teams — query and update Apex through its tools on your behalf (subject
          to your role). To connect one, generate a personal API key here and
          paste it into the client's MCP connector configuration.
        </Typography.Paragraph>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          Endpoint URL: <Typography.Text code>https://apex.oaip.nc.gov/mcp</Typography.Text>
          {" — Header: "}<Typography.Text code>X-Apex-MCP-Key</Typography.Text>
        </Typography.Paragraph>
      </Card>

      <Card title="Create a key">
        <Form layout="inline" component="div" onFinish={onCreate}>
          <Form.Item
            label="Label"
            htmlFor="mcp-key-label"
            style={{ marginBottom: 0, flex: 1 }}
          >
            <Input
              id="mcp-key-label"
              placeholder="e.g. Claude Desktop (laptop)"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onPressEnter={onCreate}
              autoComplete="off"
              maxLength={120}
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              icon={<KeyOutlined />}
              onClick={onCreate}
              loading={creating}
            >
              Create key
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {error && (
        <Card>
          <Typography.Text type="danger">Failed to load keys: {error}</Typography.Text>
        </Card>
      )}

      <Card
        title={`Your keys (${activeCount} active${keys.length > activeCount ? `, ${keys.length - activeCount} revoked` : ""})`}
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={refresh}
            loading={loading}
            size="small"
          >
            Refresh
          </Button>
        }
      >
        <Table<McpKey>
          rowKey="id"
          dataSource={keys}
          columns={columns}
          loading={loading}
          pagination={false}
          locale={{ emptyText: "No API keys yet — create one above to get started." }}
        />
      </Card>

      <Modal
        open={revealedKey !== null}
        title="Copy your new API key"
        onCancel={() => setRevealedKey(null)}
        onOk={() => setRevealedKey(null)}
        okText="I've saved it — close"
        cancelButtonProps={{ style: { display: "none" } }}
        maskClosable={false}
        keyboard={false}
        closable={false}
        width={620}
      >
        <Alert
          type="warning"
          showIcon
          message="This is the only time the full key will be shown."
          description="Copy it now and store it in your password manager. If you lose it, revoke this key and create a new one."
          style={{ marginBottom: 16 }}
        />
        <Typography.Paragraph
          copyable={{ text: revealedKey || "" }}
          style={{
            background: "#0929400d",
            padding: "12px 16px",
            borderRadius: 6,
            wordBreak: "break-all",
            fontFamily: "monospace",
            fontSize: 14,
            marginBottom: 0,
          }}
        >
          {revealedKey}
        </Typography.Paragraph>
      </Modal>
    </Space>
  );
}
